/**
 * Sentry init for the web app.
 *
 * Imported (and side-effect-executed) from main.tsx BEFORE the React tree
 * mounts so error boundaries see a configured client. Reads the DSN +
 * environment from Vite env (`import.meta.env.VITE_*`) since the value
 * is baked into the client bundle at build time — there is no runtime
 * env file in the browser.
 *
 * Behaviour
 * ---------
 *   - DSN empty → no-op, only console.* output. Use in dev or to kill
 *     uploads during a fire drill.
 *   - DSN set → captures unhandled errors, React errors thrown inside
 *     <Sentry.ErrorBoundary>, and a sampled fraction of route changes
 *     as performance traces.
 *
 * What we tag automatically
 * -------------------------
 *   - `service: hupper-web` so the org Sentry routes by app.
 *   - `release: import.meta.env.VITE_RELEASE_SHA` when set by CI.
 *   - `environment: VITE_SENTRY_ENVIRONMENT` (defaults to import.meta.env.MODE).
 */

import * as Sentry from "@sentry/react";

const DSN = (import.meta.env.VITE_SENTRY_DSN as string | undefined)?.trim() ?? "";
const ENV =
  (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined)?.trim() ??
  (import.meta.env.MODE as string | undefined) ??
  "development";
const RELEASE = (import.meta.env.VITE_RELEASE_SHA as string | undefined)?.trim() || undefined;
const TRACES = Math.max(
  0,
  Math.min(1, Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0.05)),
);

export function initSentry(): void {
  if (!DSN) {
    // eslint-disable-next-line no-console
    console.log("[sentry] DSN unset — captures disabled");
    return;
  }
  Sentry.init({
    dsn: DSN,
    environment: ENV,
    release: RELEASE,
    tracesSampleRate: TRACES,
    // Default integrations include the React error boundary helper.
    // Browser tracing instruments navigation transitions automatically.
    integrations: [Sentry.browserTracingIntegration()],
    initialScope: { tags: { service: "hupper-web" } },
    // Drop noise from third-party scripts we don't control.
    ignoreErrors: [
      // MetaMask / wallet popup dismissals — user-initiated, not bugs.
      "User rejected the request",
      "User denied",
      // Common Privy non-issues during page-leave races.
      "Network request failed",
      // ResizeObserver warnings are a no-op browser quirk.
      /ResizeObserver loop/,
    ],
  });
  // eslint-disable-next-line no-console
  console.log(
    `[sentry] initialised — env=${ENV} release=${RELEASE ?? "unset"} traces=${TRACES}`,
  );
}

/** Lazy re-export so callers can grab the React error boundary without
 *  importing the SDK directly everywhere. */
export const SentryErrorBoundary = Sentry.ErrorBoundary;
